import { ref } from "vue";
import { getInsurances, type Insurance } from "@/services/contactsApi";

// ── Shared singleton state ─────────────────────────────────────────
const allInsurances = ref<Insurance[]>([]);
const loaded = ref(false);
const loading = ref(false);

async function ensureLoaded(): Promise<Insurance[]> {
  if (loaded.value) return allInsurances.value;
  if (loading.value) {
    // Wait until the in-flight load finishes
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if (loaded.value) {
          clearInterval(check);
          resolve(allInsurances.value);
        }
      }, 50);
    });
  }
  loading.value = true;
  try {
    allInsurances.value = await getInsurances();
    loaded.value = true;
  } catch {
    allInsurances.value = [];
  } finally {
    loading.value = false;
  }
  return allInsurances.value;
}

// ── Helpers ────────────────────────────────────────────────────────

function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/[-_.,;:+&()/\\]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getDomainFromEmail(email: string | null): string | null {
  if (!email) return null;
  // Handle semicolon-separated emails — take the first one
  const parts = email.split(";");
  const first = (parts[0] || "").trim();
  if (!first) return null;
  const match = first.match(/@([^\s,;]+)/);
  return match?.[1] ?? null;
}

// Hard-coded fallback domains for insurances that have no email in the DB
const FALLBACK_DOMAINS: Record<string, string> = {
  huk: "huk.de",
  "huk-coburg": "huk-coburg.de",
  huk24: "huk24.de",
  allianz: "allianz.de",
  axa: "axa.de",
  ergo: "ergo.de",
  generali: "generali.de",
  zurich: "zurich.de",
  "r+v": "ruv.de",
  lvm: "lvm.de",
  devk: "devk.de",
  "signal iduna": "signal-iduna.de",
  gothaer: "gothaer.de",
  vhv: "vhv.de",
  württembergische: "wuerttembergische.de",
  provinzial: "provinzial.de",
  debeka: "debeka.de",
  continentale: "continentale.de",
  "da direkt": "da-direkt.de",
  hdi: "hdi.de",
  kravag: "kravag.de",
  roland: "roland-rechtsschutz.de",
  adac: "adac.de",
  arag: "arag.de",
  nürnberger: "nuernberger.de",
  cosmos: "cosmosdirekt.de",
  cosmosdirekt: "cosmosdirekt.de",
  verti: "verti.de",
  sparkassen: "sparkassenversicherung.de",
  itzehoer: "itzehoer.de",
  helvetia: "helvetia.de",
  basler: "basler.de",
  baloise: "baloise.de",
  barmenia: "barmenia.de",
  concordia: "concordia.de",
  mecklenburgische: "mecklenburgische.de",
  "alte leipziger": "alte-leipziger.de",
  volkswohl: "volkswohl-bund.de",
  bayerische: "diebayerische.de",
  "d.a.s": "das.de",
  wgv: "wgv.de",
  advocard: "advocard.de",
  "adam riese": "adam-riese.de",
  admiraldirekt: "admiraldirekt.de",
};

function getFallbackDomain(name: string): string | null {
  const n = name.toLowerCase();
  for (const [key, domain] of Object.entries(FALLBACK_DOMAINS)) {
    if (n.includes(key)) return domain;
  }
  return null;
}

// ── Public API ─────────────────────────────────────────────────────

/**
 * Match an insurance name (from case data) against the full insurance directory.
 * Returns the best-matching Insurance record, or null.
 */
export function matchInsurance(
  insuranceName: string,
  list: Insurance[]
): Insurance | null {
  if (!insuranceName) return null;
  const needle = normalize(insuranceName);

  // 1) Exact match (normalized)
  const exact = list.find((ins) => normalize(ins.name) === needle);
  if (exact) return exact;

  // 2) DB name starts with needle, or needle starts with DB name
  const startsWith = list.find(
    (ins) =>
      normalize(ins.name).startsWith(needle) ||
      needle.startsWith(normalize(ins.name))
  );
  if (startsWith) return startsWith;

  // 3) Keyword-includes match (needle contains DB name or vice versa)
  // Sort by name length descending to prefer longer (more specific) matches
  const sorted = [...list].sort((a, b) => b.name.length - a.name.length);
  const includes = sorted.find((ins) => {
    const n = normalize(ins.name);
    return needle.includes(n) || n.includes(needle);
  });
  if (includes) return includes;

  // 4) Token overlap: match if most significant words overlap
  const needleTokens = needle.split(" ").filter((t) => t.length > 2);
  let bestScore = 0;
  let bestMatch: Insurance | null = null;
  for (const ins of list) {
    const tokens = normalize(ins.name).split(" ").filter((t) => t.length > 2);
    const overlap = needleTokens.filter((t) => tokens.some((dt) => dt.includes(t) || t.includes(dt))).length;
    const score = overlap / Math.max(needleTokens.length, 1);
    if (score > bestScore && score >= 0.5) {
      bestScore = score;
      bestMatch = ins;
    }
  }
  return bestMatch;
}

/**
 * Get the logo URL for an insurance name by matching against the directory.
 * Uses Google Favicons API with domain extracted from the insurance email.
 */
export function getInsuranceLogoUrl(
  insuranceName: string,
  list: Insurance[]
): string | null {
  if (!insuranceName) return null;

  const matched = matchInsurance(insuranceName, list);
  let domain: string | null = null;

  if (matched) {
    domain = getDomainFromEmail(matched.email);
  }

  // Fallback to hardcoded domains
  if (!domain) {
    domain = getFallbackDomain(insuranceName);
  }

  if (!domain) return null;
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
}

/**
 * Composable: provides the insurance directory, matching, and logo resolution.
 */
export function useInsuranceDirectory() {
  return {
    insurances: allInsurances,
    loaded,
    loading,
    ensureLoaded,
    matchInsurance: (name: string) => matchInsurance(name, allInsurances.value),
    getLogoUrl: (name: string) => getInsuranceLogoUrl(name, allInsurances.value),
  };
}
